import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, ExternalLink, Quote, MapPin, Calendar, Image } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useLanguage } from "@/hooks/useLanguage";
import Autoplay from "embla-carousel-autoplay";

interface ReviewSource {
  name: string;
  slug: string;
  url: string;
  logo?: string;
}

const REVIEW_SOURCES: ReviewSource[] = [
  {
    name: "SafariBookings",
    slug: "safaribookings",
    url: "https://www.safaribookings.com/reviews/p3348",
  },
  {
    name: "TripAdvisor",
    slug: "tripadvisor",
    url: "https://www.tripadvisor.com/Attraction_Review-g312578-d17792104-Reviews-Shikwaru_Adventures-Johannesburg_Greater_Johannesburg_Gauteng.html",
  },
];

interface Review {
  id: string;
  reviewer_name: string;
  reviewer_country?: string | null;
  reviewer_country_code?: string | null;
  reviewer_location?: string | null;
  visited_date: string | null;
  reviewed_date: string | null;
  title: string | null;
  rating: number;
  review_text: string | null;
  photos: string[] | null;
  source?: string;
}

const ReviewsSection = () => {
  const { t, language } = useLanguage();
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // Fetch latest scrape stats for each source
  const { data: scrapeStats } = useQuery({
    queryKey: ["scrape-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scrape_logs")
        .select("source, global_rating, total_reviews, executed_at")
        .eq("status", "success")
        .order("executed_at", { ascending: false });

      if (error) throw error;

      // Get the latest entry for each source
      const latestBySource: Record<string, { global_rating: number; total_reviews: number }> = {};
      data?.forEach((log) => {
        if (!latestBySource[log.source]) {
          latestBySource[log.source] = {
            global_rating: log.global_rating || 0,
            total_reviews: log.total_reviews || 0,
          };
        }
      });

      return latestBySource;
    },
  });

  // Fetch reviews from safaribookings (only visible ones, ordered by sort_order)
  const { data: safariBookingsReviews } = useQuery({
    queryKey: ["safaribookings-reviews-preview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("safaribookings_reviews")
        .select("*")
        .eq("is_hidden", false)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(r => ({ ...r, source: 'safaribookings' })) as Review[];
    },
  });

  // Fetch reviews from tripadvisor (only visible ones, ordered by sort_order)
  const { data: tripadvisorReviews } = useQuery({
    queryKey: ["tripadvisor-reviews-preview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tripadvisor_reviews")
        .select("*")
        .eq("is_hidden", false)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(r => ({ 
        ...r, 
        source: 'tripadvisor',
        reviewer_country: r.reviewer_location 
      })) as Review[];
    },
  });

  const getReviewsForSource = (slug: string): Review[] => {
    if (slug === 'safaribookings') return safariBookingsReviews || [];
    if (slug === 'tripadvisor') return tripadvisorReviews || [];
    return [];
  };

  const decodeHtmlEntities = (text: string) => {
    return text
      .replace(/&#039;/g, "'")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">{t("reviews.title")}</h2>
          <p className="text-muted-foreground">{t("reviews.subtitle")}</p>
        </div>

        <div className="space-y-12">
          {REVIEW_SOURCES.map((source) => {
            const stats = scrapeStats?.[source.slug];
            const reviews = getReviewsForSource(source.slug);

            return (
              <div key={source.slug} className="space-y-6">
                {/* Source Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-card rounded-xl border border-border">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-primary fill-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{source.name}</h3>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                      >
                        {language === "es"
                          ? "Ver todas las reviews"
                          : language === "fr"
                            ? "Voir tous les avis"
                            : "View all reviews"}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    {stats && (
                      <>
                        <div className="text-center">
                          <div className="flex items-center gap-2">
                            <span className="text-3xl font-bold text-primary">{stats.global_rating}</span>
                            <span className="text-xl text-muted-foreground">/5</span>
                          </div>
                          <div className="flex justify-center mt-1">{renderStars(stats.global_rating)}</div>
                        </div>
                        <div className="text-center">
                          <span className="text-3xl font-bold">{stats.total_reviews}</span>
                          <p className="text-sm text-muted-foreground">
                            {language === "es" ? "reviews" : language === "fr" ? "avis" : "reviews"}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Reviews Carousel */}
                {reviews && reviews.length > 0 && (
                  <Carousel
                    opts={{
                      align: "start",
                      loop: true,
                    }}
                    plugins={[
                      Autoplay({
                        delay: 5000,
                        stopOnInteraction: true,
                      }),
                    ]}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-4">
                      {reviews.map((review) => (
                        <CarouselItem key={review.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                          <Card
                            className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full"
                            onClick={() => setSelectedReview(review)}
                          >
                            <CardContent className="p-6 flex flex-col h-full">
                              <div className="flex items-start gap-3 mb-4">
                                <Quote className="w-8 h-8 text-primary/30 flex-shrink-0 rotate-180" />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold">{decodeHtmlEntities(review.reviewer_name)}</h4>
                                    {renderStars(review.rating)}
                                  </div>
                                  {review.reviewer_country && (
                                    <p className="text-sm text-muted-foreground">{review.reviewer_country}</p>
                                  )}
                                </div>
                              </div>

                              {review.title && (
                                <h5 className="font-medium mb-2 text-foreground">
                                  {truncateText(decodeHtmlEntities(review.title), 60)}
                                </h5>
                              )}

                              {review.review_text && (
                                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                                  {truncateText(decodeHtmlEntities(review.review_text), 200)}
                                </p>
                              )}

                              {review.photos && review.photos.length > 0 && (
                                <div className="flex gap-2 mt-4 overflow-x-auto">
                                  {review.photos.slice(0, 3).map((photo, idx) => (
                                    <img
                                      key={idx}
                                      src={photo}
                                      alt={`Review photo ${idx + 1}`}
                                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                    />
                                  ))}
                                  {review.photos.length > 3 && (
                                    <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                                      <span className="text-sm font-medium text-muted-foreground">
                                        +{review.photos.length - 3}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="hidden md:flex -left-12" />
                    <CarouselNext className="hidden md:flex -right-12" />
                  </Carousel>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Review Detail Dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {language === "es" ? "Detalle de Review" : language === "fr" ? "Détail de l'avis" : "Review Detail"}
            </DialogTitle>
          </DialogHeader>

          {selectedReview && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6">
                {/* Header with name and rating */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold">{decodeHtmlEntities(selectedReview.reviewer_name)}</h3>
                    {selectedReview.reviewer_country && (
                      <div className="flex items-center gap-1 text-muted-foreground mt-1">
                        <MapPin className="h-4 w-4" />
                        <span>{selectedReview.reviewer_country}</span>
                        {selectedReview.reviewer_country_code && (
                          <Badge variant="outline" className="ml-2">
                            {selectedReview.reviewer_country_code}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={20}
                          className={star <= selectedReview.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedReview.rating}/5
                    </p>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedReview.visited_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {language === "es" ? "Visitó:" : language === "fr" ? "A visité:" : "Visited:"}
                      </span>
                      <span className="font-medium">{selectedReview.visited_date}</span>
                    </div>
                  )}
                  {selectedReview.reviewed_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {language === "es" ? "Reseñó:" : language === "fr" ? "Avis:" : "Reviewed:"}
                      </span>
                      <span className="font-medium">{selectedReview.reviewed_date}</span>
                    </div>
                  )}
                </div>

                {/* Title */}
                {selectedReview.title && (
                  <h4 className="font-semibold text-lg">{decodeHtmlEntities(selectedReview.title)}</h4>
                )}

                {/* Review Text */}
                {selectedReview.review_text && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {decodeHtmlEntities(selectedReview.review_text)}
                    </p>
                  </div>
                )}

                {/* Photos */}
                {selectedReview.photos && selectedReview.photos.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      {language === "es" ? "Fotos" : language === "fr" ? "Photos" : "Photos"} ({selectedReview.photos.length})
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedReview.photos.map((photo, index) => (
                        <a
                          key={index}
                          href={photo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="aspect-square rounded-lg overflow-hidden bg-muted hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={photo}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ReviewsSection;
